<template>
	<div class="route-content dashboard-route">
		<!-- Custom: Ziniuzygis old user removal notification -->
		<transition-height name="fade">
			<div v-if="showRemovalNotice" class="news-promo-alert" style="cursor: unset">
				<div class="title">
					Žygiuotojau, nesnausk!
				</div>
				<div class="intro" style="display: unset!important;">
					Tavo 23 gimtadienis jau visai netrukus - baigiasi narystė Jaunimo programoje, o kartu finišuoji ir Žinių žygyje. Iškeisk taškus į prizus, kol dar gali.
				</div>
			</div>
		</transition-height>

		<ActiveSessionsWarning v-if="isMobile && userStore.activeSessions > 1" />
		<NewsPromoAlert v-if="settings.newsEnabled" />
		<NotCollectedAchievementsBlock />

		<div class="row">
			<div class="col-xs-8 block-title">
				{{ t.dashboardTitle }}
			</div>
			<div v-if="showOwnerImage" class="col-xs-4 text-right">
				<img :src="ownerImage" alt="" class="owner-logo">
			</div>
		</div>

		<!-- Owner challenge list -->
		<div v-if="ownerId" class="challenge-list list-owner">
			<challenge v-for="item in challengesOfOwner" :key="item.id" :challenge="item" @challenge-opened="openChallenge" />
			<challenges-completed-block v-if="!showAllChallenges && (!challengesOfOwner || !challengesOfOwner.length)" />
		</div>

		<div v-if="ownerId && !showAllChallenges" class="text-center mt-3">
			<button type="button" class="btn-primary" @click="showAllChallenges = true">
				{{ t.dashboardShowMoreChallengesButton }}
			</button>
		</div>

		<!-- Generic challenge list -->
		<transition name="fade">
			<InfiniteList v-show="showAllChallenges" :handler="load" class="challenge-list list-general">
				<challenge v-for="item in challengesGenericList" :key="item.id" :challenge="item" @challenge-opened="openChallenge" />

				<template #spinner>
					<div class="m-5">
						<Loader />
					</div>
				</template>

				<template #complete>
					<push-notification-promo />

					<div class="challenge-list list-footer">
						{{ t.dashboardListFooter }}
					</div>
				</template>

				<template #no-items>
					<challenges-completed-block />
				</template>
			</InfiniteList>
		</transition>
	</div>
</template>

<script setup>
import { computed, inject, onBeforeUnmount, onMounted, ref } from 'vue'
import Challenge from '@/js/components/dashboard/challenge.vue'
import Loader from '../components/loader.vue'
import PushNotificationPromo from '@/js/components/dashboard/push-notification-promo.vue'
import ChallengesCompletedBlock from '@/js/components/dashboard/challenges-completed-block.vue'
import NewsPromoAlert from '@/js/components/blocks/news-promo-alert.vue'
import ActiveSessionsWarning from '@/js/components/blocks/active-sessions-warning.vue'
import NotCollectedAchievementsBlock from '../components/dashboard/not-collected-achievements-block.vue'
import InfiniteList from '../components/infinite-list.vue'
import queryString from 'query-string'
import { isRealObject } from "@/js/includes/helpers"
import { $on } from '../includes/event-bus'
import { useStore } from '@/js/stores/index';
import { useChallenges } from '@/js/stores/challenges';
import { useUserStore } from '../stores/user'
import dayjs from 'dayjs'

const axios = inject('axios')
const store = useStore()
const userStore = useUserStore()
const { addChallengeItem, list, idsList } = useChallenges()

// We parse it here and not in mounted() because moving across other urls will clean initial values
var qs = queryString.parse(location.search)

let itemLoadingTimeout = false
let loaderPageSize = 10

// Reactives
const challengeOpened = ref(false)
const showAllChallenges = ref(false)
const showChallengesAsNew = ref([])
const challengesLoadTimestamp = ref(0)

// Computeds
const t = computed(() => store.translations)
const isMobile = computed(() => store.isMobile)
const ownerId = computed(() => store.owner.id)
const challenges = computed(() => list)
const challengesIds = computed(() => idsList)
const ownerImage = computed(() => store.owner.imageId)
const settings = computed(() => store.settings)
const challengesOfOwner = computed(() => list?.filter(item => item.challenge_owner_id == ownerId.value))
const challengesGenericList = computed(() => list?.filter(item => !item.challenge_owner_id || item.challenge_owner_id != ownerId.value))
const showOwnerImage = computed(() => {
	if(!ownerImage.value) return false;
	if(challengesOfOwner.value.length) return true;
	if(!challengesOfOwner.value.length && !showAllChallenges.value) return true;

	return false
})

// Custom Ziniuzygis old user removal notice
const showRemovalNotice = computed(() => userStore?.birthday
	? dayjs(userStore.birthday).add(22, "year").add(11, "month") < dayjs()
		&& dayjs(userStore.birthday).add(23, "year") > dayjs() // If the user is older than 23 it is likely a "leftover" exception which are handled manually
	: false)

// Methods
// Load user challenge items from server
async function _loadChallenges(url)
{
	const { data, headers } = await axios.get(url)
	let added = 0

	// Save last challenge list loading time 
	challengesLoadTimestamp.value = Date.parse(headers.date)

	// Go through the list and add items found
	data.forEach(item => {
		// Set "new" tag if user came from push notification or email and bring "new_challenges" parameter
		if(showChallengesAsNew.value.indexOf(item.id.toString()) !== -1){
			item.tags.push("new")
		}
		
		addItem(item) ? added++ : false
	})
	
	// Return number of added elements
	return added
}

async function load(state)
{
	// Try to load available challenges starting from last position in the list. But we make -1 here, as if challenge 
	// get completed interactively, indexes may be shifted by 1 up.
	const loadedCount = await _loadChallenges('/api/user-side/getUserChallenges?limit='+loaderPageSize+'&start_from=' + (challenges.value?.length || 0 - 1))

	/**
	* Resolve list states
	*/
	if(!list.length && !loadedCount) state.empty()
	if(loadedCount < loaderPageSize) state.complete()
}

async function loadNewItems(){
	return await _loadChallenges('/api/user-side/getUserChallenges?last_timestamp='	+ (challengesLoadTimestamp.value / 1000))
}

function nextLoadNewItems(timeout){
	// Stop all previous executions
	clearTimeout(itemLoadingTimeout)

	// Set new timer
	itemLoadingTimeout = setTimeout(async () => {
		const loaded = await loadNewItems()
		
		// If we got something from server. It's enough
		if(loaded) clearTimeout(itemLoadingTimeout)

		// Don't operate with timeouts less than a second
		if(timeout < 1000) timeout = 1000

		// Make timeout incrementally longer
		nextLoadNewItems(timeout * 2)
	}, timeout)
}

function addItem(item){
	if(challengesIds.value?.indexOf(item.id) >= 0){ // Skip already loaded challenge
		return false
	}
	
	// Ensure custom_data value is an object
	if(!isRealObject(item.custom_data)){
		item.custom_data = {}
	}

	// Set progress bar settings if needed
	if(item.custom_data && item.progress_divisor){
		const base = Math.min(item.progress_dividend || 0, item.progress_divisor)
		item.progressShare = base / item.progress_divisor * 100
		item.progressLabel = base + ' / ' + item.progress_divisor
	}

	// Save challenge loading time for debugging
	item.loadingTime = new Date()

	addChallengeItem(item)

	return true;
}

function openChallenge(item){
	challengeOpened.value = item.id
}

// Lifecycle
onMounted(() => {
	if(!userStore.loggedIn){
		// Do not re-mount if the user is logged off. It prevents remounting on logoff
		return;
	}

	// Detect challenges to mark as "new"
	if(qs.new_challenges){
		showChallengesAsNew.value = qs.new_challenges.split(',')
	}

	// Let's show all challenges if no it's not an owned challenge list
	if(!ownerId.value) showAllChallenges.value = true
})

onBeforeUnmount(() => {
	// Clear delayed executions to prevent exceptions
	clearTimeout(itemLoadingTimeout)
})

// Events
$on('challenge-completed', challenge => {
	// Change challenge display to "completed"
	challenge.showBlock = "complete"

	// Try asking for challenges with delay
	nextLoadNewItems(1000)
})
</script>
